/**
 * Helpers for distinguishing special item types from regular rentals.
 *
 * Categories:
 *   - sale items     : SKU starts with "vente"    (flat price, no /jour, no insurance)
 *   - vehicle items  : SKU starts with "vehicule" (no insurance — own franchise scheme)
 *   - parking items  : SKU starts with "park"     (no insurance)
 *
 * "Regular rentals" are everything else, and they bear the 8% insurance.
 */
/**
 * True when the product belongs to the OCCASIONS branch. Occasions are sold
 * flat-priced too, but they don't share the `vente*` SKU prefix so we detect
 * them via the nav-menu field pushed by the back-office catalogue.
 */
export const isOccasionProduct = (product) => {
  if (!product) return false;
  const nav = String(product.nav_menu || '').toUpperCase();
  if (nav === 'OCCASIONS') return true;
  const extra = product.additional_nav || [];
  return Array.isArray(extra) && extra.some((n) => String(n).toUpperCase() === 'OCCASIONS');
};

/**
 * True for anything that should display a flat "HT" price (no /jour):
 * sales AND occasion items.
 */
export const isFlatPriced = (product) => isSaleSku(product?.sku) || isOccasionProduct(product);

const startsWithLower = (val, prefix) =>
  Boolean(val) && String(val).toLowerCase().startsWith(prefix);

const skuOf = (item) => item?.product_sku || item?.sku || item?.product_id;

export const isSaleSku = (sku) => startsWithLower(sku, 'vente');
export const isVehicleSku = (sku) =>
  startsWithLower(sku, 'vehicule') || startsWithLower(sku, 'cam20') || startsWithLower(sku, 'cam12');
export const isParkingSku = (sku) => startsWithLower(sku, 'park');

/**
 * Vehicle-linked SKUs whose price MUST scale linearly with business days
 * (no day-coefficient). Covers the vehicles themselves (cam20, cam12) and
 * their dedicated options (chauffeur camcond, franchise buyback camfranp /
 * camfrant).
 */
const COEF_EXEMPT_PREFIXES = ['cam20', 'cam12', 'camcond', 'camfranp', 'camfrant'];
export const isCoefExemptSku = (sku) =>
  COEF_EXEMPT_PREFIXES.some((p) => startsWithLower(sku, p));

export const isSaleItem       = (item) => isSaleSku(skuOf(item));
export const isVehicleItem    = (item) => isVehicleSku(skuOf(item));
export const isParkingItem    = (item) => isParkingSku(skuOf(item));
export const isCoefExemptItem = (item) => isCoefExemptSku(skuOf(item));

/** True for any item that should NOT be charged the 8% insurance. */
export const isInsuranceExempt = (item) =>
  isSaleItem(item) || isVehicleItem(item) || isParkingItem(item);

/**
 * Split cart items into buckets. Sales are flat-priced. Rentals share the
 * day-multiplier. We keep "rentals" as the catch-all category for sums.
 */
export const splitCartItems = (items = []) => {
  const rentals = [];
  const sales = [];
  const vehicles = [];
  const parkings = [];
  for (const it of items) {
    if (isSaleItem(it)) sales.push(it);
    else if (isVehicleItem(it)) { rentals.push(it); vehicles.push(it); }
    else if (isParkingItem(it)) { rentals.push(it); parkings.push(it); }
    else rentals.push(it);
  }
  return { rentals, sales, vehicles, parkings };
};

/** Sum of `daily_price * quantity` for a list. */
export const sumLineTotal = (items = []) =>
  items.reduce((s, it) => s + Number(it.daily_price || 0) * Number(it.quantity || 0), 0);

/**
 * Sum of `daily_price * quantity` restricted to items whose SKU IS exempt
 * from the day-coefficient (vehicles + vehicle options).
 */
export const sumCoefExemptLineTotal = (items = []) =>
  items
    .filter(isCoefExemptItem)
    .reduce((s, it) => s + Number(it.daily_price || 0) * Number(it.quantity || 0), 0);

/**
 * Sum of `daily_price * quantity` restricted to items where the day
 * coefficient DOES apply (regular rentals — vehicles and their options
 * excluded).
 */
export const sumCoefAppliedLineTotal = (items = []) =>
  items
    .filter((it) => !isCoefExemptItem(it))
    .reduce((s, it) => s + Number(it.daily_price || 0) * Number(it.quantity || 0), 0);

/**
 * Subtotal of insurance-eligible rentals only — i.e. rentals minus vehicles
 * and parking spots. Used to compute the 8% assurance.
 */
export const sumInsurableLineTotal = (items = []) =>
  items
    .filter((it) => !isInsuranceExempt(it))
    .reduce((s, it) => s + Number(it.daily_price || 0) * Number(it.quantity || 0), 0);
